import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { IAssetsDAO, ITitleAssetUrlDTO } from '../../../../../api/assets.dao';
import { Space } from '../../../../../constants/html-character-entities';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';

export class AssetsFileFormViewModel {
    @mobx.observable
    public file: ITitleAssetUrlDTO;
    @mobx.observable
    public isSpeakerAsset: boolean;
    @mobx.observable
    public isLoadingDownload: boolean;
    public permissionToUpdate: boolean;
    public permissionToDownload: boolean;
    @mobx.observable
    public assetDownloadUrl: string;

    constructor(
        public readonly accountId: number,
        public readonly eventId: number,
        public readonly organizationId: number | null,
        public readonly attendeeId: number | null,
        file: ITitleAssetUrlDTO,
        private closeAssetsFilePopup: () => void,
        private onDeleteAsset: (objectKey: string) => void,
        private uploadProfileImage: (file: File, organizationId: number, attendeeId: number) => void | null,
        private readonly isFileProtected: boolean,
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly assetsDAO: IAssetsDAO = IoC.Container.get(IAssetsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.file = file || null;
        this.isSpeakerAsset = this.organizationId !== null && this.attendeeId !== null;
        this.isLoadingDownload = false;
        this.permissionToUpdate = this.isSpeakerAsset ? hasPermission(Permission.UPDATE_ATTENDEE) : hasPermission(Permission.DELETE_EVENT_ASSETS);
        this.permissionToDownload = hasPermission(Permission.DOWNLOAD_EVENT_ASSETS);
        this.initFileDownloadUrl();
    }

    @mobx.action
    private setAssetDownloadUrl(url: string): void {
        this.assetDownloadUrl = url;
    }

    public get fileDownloadAllowed(): boolean {
        return hasPermission(Permission.DOWNLOAD_EVENT_ASSETS);
    }

    @mobx.action.bound
    private async initFileDownloadUrl(): Promise<void> {
        if (this.isFileProtected) {
            await this.setProtectedAssetDownloadUrl();
        } else {
            this.setAssetDownloadUrl(this.file.assetUrl);
        }
    }

    @mobx.action.bound
    private async setProtectedAssetDownloadUrl(): Promise<void> {
        if (this.fileDownloadAllowed) {
            try {
                const { downloadUrl } = await this.assetsDAO.getAssetFileDownloadUrl({
                    accountId: this.accountId,
                    eventId: this.eventId,
                    assetMetadataId: this.file.assetMetadataId
                });
                this.setAssetDownloadUrl(downloadUrl);
            } catch {
                this.setAssetDownloadUrl('');
            }
        } else {
            this.setAssetDownloadUrl('');
        }
    }

    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @mobx.action
    public downloadAsset = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isLoadingDownload = true;
            });
            await this.assetsDAO.getAssetFile(this.assetDownloadUrl, this.file.title);
            this.trackAssetFileMixPanel(TRACKING_EVENTS.DOWNLOAD_EVENT_ASSET);
            this.closeAssetsFilePopup();
        } catch (e) {
            AssetsFileFormViewModel.handleApiException(e);
        } finally {
            mobx.runInAction(() => {
                this.isLoadingDownload = false;
            });
        }
    };

    private completeAssetDeletion = (objectKey: string): void => {
        this.onDeleteAsset(objectKey);
        this.trackAssetFileMixPanel(TRACKING_EVENTS.DELETE_EVENT_ASSET);
        this.closeAssetsFilePopup();
    };

    private deleteAssetApiCall = async (): Promise<void> => {
        try {
           if (this.isSpeakerAsset) {
               await this.assetsDAO.deleteSpeakerAssetFile({ accountId: this.accountId, eventId: this.eventId, attendeeId: this.attendeeId});
           } else await this.assetsDAO.deleteAssetFile({
                    accountId: this.accountId,
                    eventId: this.eventId,
                    ...(this.file.assetMetadataId && { assetMetadataId: this.file.assetMetadataId})}
            );
            this.completeAssetDeletion(this.file.objectKey);
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    private removeMessages = (): { message: React.ReactNode; confirmDelete: React.ReactNode } => {
        if (this.isSpeakerAsset) {
            return {
                message:
                    <span className='alert-message'>
                        <FormattedMessage
                            id='oe.confirm.doYouWantTo'
                            description='Confirm message'
                            defaultMessage='Do you want to'
                        />
                        <Space />
                        <span className='alert-message--danger'>
                            <FormattedMessage
                                id='oe.file.deleteSpeakerImageFromEvent'
                                description='Confirm message'
                                defaultMessage='remove this image from the Speaker and from the Event?'
                            />
                        </span>
                        <Space />
                        <FormattedMessage
                            id='oe.confirm.removedFilesCannotBeRecovered'
                            description='Confirm message'
                            defaultMessage='Removed files cannot be recovered.'
                        />
                    </span>,
                confirmDelete:
                    <FormattedMessage
                        id='oe.file.removeFile'
                        description='label text'
                        defaultMessage='Remove File'
                    />
            };
        } else {
            return {
                message:
                    <span className='alert-message'>
                        <FormattedMessage
                            id='oe.confirm.areYouSureTo'
                            description='Confirm message'
                            defaultMessage='Are you sure you want to'
                        />
                        <Space />
                        <span className='alert-message--danger'>
                            <FormattedMessage
                                id='oe.file.deleteThisFile'
                                description='Confirm message'
                                defaultMessage='Delete this File'
                            />
                        </span>
                        ?
                    </span>,
                confirmDelete:
                    <FormattedMessage
                        id='oe.file.deleteFile'
                        description='label text'
                        defaultMessage='Yes, Delete'
                    />
            };
        }
    };

    @mobx.action
    public deleteAssetImage = (): void => {
        const { message, confirmDelete } = this.removeMessages();
        this.alertsService.confirm({
            message: message,
            confirmButtonProps: {
                text: confirmDelete,
                modifier: 'confirm-warn',
                handler: this.deleteAssetApiCall
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private replaceSpeakerAssetApiCall = async (file: File): Promise<void> => {
        try {
            if (this.isSpeakerAsset) {
                await this.uploadProfileImage(file, this.organizationId, this.attendeeId);
                this.closeAssetsFilePopup();
            }
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
    };

    @mobx.action
    public replaceSpeakerAssetImage = (file: File): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.doYouWantTo'
                        description='Confirm message'
                        defaultMessage='Do you want to'
                    />
                    <Space />
                    <span className='alert-message--confirm-safe'>
                        <FormattedMessage
                            id='oe.user.replaceThisImage'
                            description='Confirm message'
                            defaultMessage='replace this image'
                        />
                    </span>
                    ? <Space />
                    <FormattedMessage
                        id='oe.confirm.imageCannotBeRecovered'
                        description='Confirm message'
                        defaultMessage='The original image will be deleted and cannot be recovered'
                    />.
                </span>
            ),
            confirmButtonProps: {
                text: 'Replace',
                modifier: 'confirm-safe',
                handler: () => this.replaceSpeakerAssetApiCall(file)
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    public getMixinAccountNameAndEventId = (): { [key: string]: string } => ({
        'Account Name': this.globalStorage.getEvent()?.accountName,
        'Event ID': this.eventId.toString(),
        'Event Name': this.globalStorage.getEvent()?.name,
    });

    public trackAssetFileMixPanel = (trackingEvent: TRACKING_EVENTS): void => {
        const currentUrl = window.location.href;
        this.trackingService.get().track(trackingEvent, {
            ...this.getMixinAccountNameAndEventId(),
            ...(this.isSpeakerAsset && { 'Attendee ID': this.attendeeId }),
            'Event URL': currentUrl,
        });
    };
}
